import React, { createContext, useContext, useState, useEffect, startTransition } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    
    // Update CSS custom properties for comprehensive theme switching
    const root = document.documentElement;
    if (isDarkMode) {
      // Dark mode colors
      root.style.setProperty('--body-background', '#0f172a');
      root.style.setProperty('--app-background', 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #1e293b 75%, #0f172a 100%)');
      root.style.setProperty('--header-background', 'rgba(15, 23, 42, 0.95)');
      root.style.setProperty('--footer-background', 'rgba(15, 23, 42, 0.95)');
      root.style.setProperty('--button-primary', 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)');
      root.style.setProperty('--title-gradient', 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 25%, #d97706 50%, #f59e0b 75%, #fbbf24 100%)');
      
      // Auth page specific colors for dark mode
      root.style.setProperty('--auth-container-background', 'linear-gradient(135deg, #0f172a 0%, #1e293b 25%, #334155 50%, #1e293b 75%, #0f172a 100%)');
      root.style.setProperty('--auth-card-background', 'rgba(30, 41, 59, 0.98)');
      root.style.setProperty('--auth-card-border', 'rgba(148, 163, 184, 0.2)');
      root.style.setProperty('--auth-left-section-background', 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)');
      root.style.setProperty('--auth-right-section-background', '#1e293b');
      root.style.setProperty('--auth-input-background', '#334155');
      root.style.setProperty('--auth-input-border', '#475569');
      root.style.setProperty('--auth-input-focus-border', '#8b5cf6');
      root.style.setProperty('--auth-input-color', '#f1f5f9');
      root.style.setProperty('--auth-input-placeholder-color', '#94a3b8');
      root.style.setProperty('--auth-label-color', '#e2e8f0');
      root.style.setProperty('--auth-tab-border', '#334155');
      root.style.setProperty('--auth-tab-active-color', '#8b5cf6');
      root.style.setProperty('--auth-tab-inactive-color', '#94a3b8');
      root.style.setProperty('--auth-checkbox-background', '#334155');
      root.style.setProperty('--auth-checkbox-border', '#475569');
      root.style.setProperty('--auth-checkbox-label-color', '#cbd5e1');
      root.style.setProperty('--auth-link-color', '#8b5cf6');
      root.style.setProperty('--auth-switch-text-color', '#94a3b8');
      root.style.setProperty('--auth-error-color', '#f87171');
      root.style.setProperty('--auth-success-background', '#064e3b');
      root.style.setProperty('--auth-success-border', '#065f46');
      root.style.setProperty('--auth-success-color', '#6ee7b7');
      root.style.setProperty('--auth-file-upload-background', '#334155');
      root.style.setProperty('--auth-file-upload-border', '#475569');
      root.style.setProperty('--auth-file-upload-hover-background', '#475569');
      root.style.setProperty('--auth-file-upload-text-color', '#e2e8f0');
      root.style.setProperty('--auth-file-upload-subtext-color', '#94a3b8');
      root.style.setProperty('--auth-password-toggle-color', '#94a3b8');
      root.style.setProperty('--auth-password-toggle-hover-background', 'rgba(139, 92, 246, 0.1)');
      root.style.setProperty('--auth-password-toggle-hover-color', '#8b5cf6');
      root.style.setProperty('--auth-otp-button-background', 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)');
      root.style.setProperty('--auth-otp-input-background', '#334155');
      root.style.setProperty('--auth-otp-input-border', '#475569');
      root.style.setProperty('--auth-otp-input-focus-border', '#8b5cf6');
      root.style.setProperty('--auth-otp-verify-button-background', '#059669');
      root.style.setProperty('--auth-select-background', '#334155');
      root.style.setProperty('--auth-select-border', '#475569');
      root.style.setProperty('--auth-select-focus-border', '#8b5cf6');
      root.style.setProperty('--auth-select-color', '#f1f5f9');
      root.style.setProperty('--auth-date-picker-background', '#1e293b');
      root.style.setProperty('--auth-date-picker-border', '#475569');
      root.style.setProperty('--auth-date-picker-header-background', '#334155');
      root.style.setProperty('--auth-date-picker-title-color', '#f1f5f9');
      root.style.setProperty('--auth-date-picker-day-header-color', '#94a3b8');
      root.style.setProperty('--auth-date-picker-day-color', '#f1f5f9');
      root.style.setProperty('--auth-date-picker-day-today-color', '#8b5cf6');
      root.style.setProperty('--auth-date-picker-day-selected-background', '#8b5cf6');
      root.style.setProperty('--auth-date-picker-day-selected-color', '#ffffff');
      root.style.setProperty('--auth-date-picker-day-other-month-color', '#64748b');
      root.style.setProperty('--auth-date-picker-button-color', '#8b5cf6');
      root.style.setProperty('--auth-date-picker-select-background', '#1e293b');
      root.style.setProperty('--auth-date-picker-select-border', '#475569');
      root.style.setProperty('--auth-date-picker-select-color', '#f1f5f9');
      root.style.setProperty('--auth-success-title-color', '#f1f5f9');
      root.style.setProperty('--auth-success-message-color', '#cbd5e1');
      root.style.setProperty('--auth-success-details-background', '#334155');
      root.style.setProperty('--auth-success-button-background', 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)');
      root.style.setProperty('--auth-file-preview-background', '#334155');
      root.style.setProperty('--auth-secondary-button-background', 'transparent');
      root.style.setProperty('--auth-secondary-button-border', '#8b5cf6');
      root.style.setProperty('--auth-secondary-button-color', '#8b5cf6');
      root.style.setProperty('--auth-secondary-button-hover-background', '#8b5cf6');
      root.style.setProperty('--auth-secondary-button-hover-color', '#ffffff');
      
      // Profile page colors for dark mode
      root.style.setProperty('--profile-background', 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)');
      root.style.setProperty('--profile-card-background', 'rgba(255, 255, 255, 0.05)');
      root.style.setProperty('--profile-card-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--avatar-background', 'linear-gradient(135deg, #2d3561 0%, #3d4a7c 100%)');
      root.style.setProperty('--avatar-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--profile-name-color', '#ffffff');
      root.style.setProperty('--profile-email-color', 'rgba(255, 255, 255, 0.8)');
      root.style.setProperty('--section-title-color', '#ffffff');
      root.style.setProperty('--info-item-background', 'rgba(255, 255, 255, 0.03)');
      root.style.setProperty('--info-item-border', 'rgba(255, 255, 255, 0.08)');
      root.style.setProperty('--info-label-color', 'rgba(255, 255, 255, 0.6)');
      root.style.setProperty('--info-value-color', '#ffffff');
      root.style.setProperty('--id-card-background', 'rgba(255, 255, 255, 0.03)');
      root.style.setProperty('--id-card-border', 'rgba(255, 255, 255, 0.08)');
      root.style.setProperty('--id-card-title-color', '#ffffff');
      root.style.setProperty('--id-card-image-border', 'rgba(255, 255, 255, 0.15)');
      root.style.setProperty('--upload-button-background', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--upload-button-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--upload-button-color', '#ffffff');
      root.style.setProperty('--upload-button-hover-background', 'rgba(255, 255, 255, 0.15)');
      root.style.setProperty('--upload-button-hover-border', 'rgba(255, 255, 255, 0.3)');
      root.style.setProperty('--loading-spinner-color', '#ffffff');
      
      // Contact page colors for dark mode
      root.style.setProperty('--contact-background', 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)');
      root.style.setProperty('--contact-title-color', '#ffffff');
      root.style.setProperty('--contact-subtitle-color', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--contact-card-background', 'rgba(255, 255, 255, 0.05)');
      root.style.setProperty('--contact-card-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--contact-info-title-color', '#ffffff');
      root.style.setProperty('--contact-info-label-color', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--contact-info-value-color', 'rgba(255, 255, 255, 0.8)');
      root.style.setProperty('--contact-form-background', 'rgba(255, 255, 255, 0.05)');
      root.style.setProperty('--contact-form-title-color', '#ffffff');
      root.style.setProperty('--contact-form-label-color', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--contact-form-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--contact-form-input-background', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--contact-form-input-color', '#ffffff');
      root.style.setProperty('--contact-form-placeholder-color', 'rgba(255, 255, 255, 0.6)');
      root.style.setProperty('--contact-form-focus-border', '#667eea');
      root.style.setProperty('--social-link-background', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--social-link-color', '#ffffff');
      root.style.setProperty('--social-link-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--social-link-hover-background', 'rgba(255, 255, 255, 0.2)');
      
      root.setAttribute('data-theme', 'dark');
    } else {
      root.style.setProperty('--body-background', '#3b82f6');
      root.style.setProperty('--app-background', 'linear-gradient(135deg, #1e40af 25%, #1e40af 50%, #1e40af 100%)');
      root.style.setProperty('--header-background', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--footer-background', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--button-primary', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--title-gradient', 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 25%, #e0f2fe 50%, #f0f9ff 75%, #ffffff 100%)');
      
      // Auth page specific colors for light mode
      root.style.setProperty('--auth-container-background', 'linear-gradient(135deg, #667eea 0%, #1e3a8a 50%, #1e40af 100%)');
      root.style.setProperty('--auth-card-background', 'rgba(255, 255, 255, 0.98)');
      root.style.setProperty('--auth-card-border', 'rgba(255, 255, 255, 0.3)');
      root.style.setProperty('--auth-left-section-background', 'linear-gradient(135deg, #667eea 0%, #1e3a8a 50%, #1e40af 100%)');
      root.style.setProperty('--auth-right-section-background', '#ffffff');
      root.style.setProperty('--auth-input-background', '#ffffff');
      root.style.setProperty('--auth-input-border', '#e0e0e0');
      root.style.setProperty('--auth-input-focus-border', '#667eea');
      root.style.setProperty('--auth-input-color', '#333333');
      root.style.setProperty('--auth-input-placeholder-color', '#999999');
      root.style.setProperty('--auth-label-color', '#333333');
      root.style.setProperty('--auth-tab-border', '#f0f0f0');
      root.style.setProperty('--auth-tab-active-color', '#667eea');
      root.style.setProperty('--auth-tab-inactive-color', '#999999');
      root.style.setProperty('--auth-checkbox-background', '#ffffff');
      root.style.setProperty('--auth-checkbox-border', '#e0e0e0');
      root.style.setProperty('--auth-checkbox-label-color', '#666666');
      root.style.setProperty('--auth-link-color', '#667eea');
      root.style.setProperty('--auth-switch-text-color', '#666666');
      root.style.setProperty('--auth-error-color', '#ff6b6b');
      root.style.setProperty('--auth-success-background', '#d4edda');
      root.style.setProperty('--auth-success-border', '#c3e6cb');
      root.style.setProperty('--auth-success-color', '#155724');
      root.style.setProperty('--auth-file-upload-background', '#fafafa');
      root.style.setProperty('--auth-file-upload-border', '#e0e0e0');
      root.style.setProperty('--auth-file-upload-hover-background', '#f8f9ff');
      root.style.setProperty('--auth-file-upload-text-color', '#666666');
      root.style.setProperty('--auth-file-upload-subtext-color', '#999999');
      root.style.setProperty('--auth-password-toggle-color', '#666666');
      root.style.setProperty('--auth-password-toggle-hover-background', 'rgba(102, 126, 234, 0.1)');
      root.style.setProperty('--auth-password-toggle-hover-color', '#667eea');
      root.style.setProperty('--auth-otp-button-background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--auth-otp-input-background', '#ffffff');
      root.style.setProperty('--auth-otp-input-border', '#e0e0e0');
      root.style.setProperty('--auth-otp-input-focus-border', '#667eea');
      root.style.setProperty('--auth-otp-verify-button-background', '#28a745');
      root.style.setProperty('--auth-select-background', '#ffffff');
      root.style.setProperty('--auth-select-border', '#e0e0e0');
      root.style.setProperty('--auth-select-focus-border', '#667eea');
      root.style.setProperty('--auth-select-color', '#333333');
      root.style.setProperty('--auth-date-picker-background', '#ffffff');
      root.style.setProperty('--auth-date-picker-border', '#e0e0e0');
      root.style.setProperty('--auth-date-picker-header-background', '#f8f9ff');
      root.style.setProperty('--auth-date-picker-title-color', '#333333');
      root.style.setProperty('--auth-date-picker-day-header-color', '#666666');
      root.style.setProperty('--auth-date-picker-day-color', '#333333');
      root.style.setProperty('--auth-date-picker-day-today-color', '#667eea');
      root.style.setProperty('--auth-date-picker-day-selected-background', '#667eea');
      root.style.setProperty('--auth-date-picker-day-selected-color', '#ffffff');
      root.style.setProperty('--auth-date-picker-day-other-month-color', '#cccccc');
      root.style.setProperty('--auth-date-picker-button-color', '#667eea');
      root.style.setProperty('--auth-date-picker-select-background', '#ffffff');
      root.style.setProperty('--auth-date-picker-select-border', '#e0e0e0');
      root.style.setProperty('--auth-date-picker-select-color', '#333333');
      root.style.setProperty('--auth-success-title-color', '#333333');
      root.style.setProperty('--auth-success-message-color', '#666666');
      root.style.setProperty('--auth-success-details-background', '#f8f9ff');
      root.style.setProperty('--auth-success-button-background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--auth-file-preview-background', '#f8f9ff');
      root.style.setProperty('--auth-secondary-button-background', 'transparent');
      root.style.setProperty('--auth-secondary-button-border', '#667eea');
      root.style.setProperty('--auth-secondary-button-color', '#667eea');
      root.style.setProperty('--auth-secondary-button-hover-background', '#667eea');
      root.style.setProperty('--auth-secondary-button-hover-color', '#ffffff');
      
      // Profile page colors
      root.style.setProperty('--profile-background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--profile-card-background', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--profile-card-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--avatar-background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--avatar-border', 'rgba(255, 255, 255, 0.3)');
      root.style.setProperty('--profile-name-color', '#ffffff');
      root.style.setProperty('--profile-email-color', 'rgba(255, 255, 255, 0.8)');
      root.style.setProperty('--section-title-color', '#ffffff');
      root.style.setProperty('--info-item-background', 'rgba(255, 255, 255, 0.05)');
      root.style.setProperty('--info-item-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--info-label-color', 'rgba(255, 255, 255, 0.7)');
      root.style.setProperty('--info-value-color', '#ffffff');
      root.style.setProperty('--id-card-background', 'rgba(255, 255, 255, 0.05)');
      root.style.setProperty('--id-card-border', 'rgba(255, 255, 255, 0.1)');
      root.style.setProperty('--id-card-title-color', '#ffffff');
      root.style.setProperty('--id-card-image-border', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--upload-button-background', 'rgba(255, 255, 255, 0.2)');
      root.style.setProperty('--upload-button-border', 'rgba(255, 255, 255, 0.3)');
      root.style.setProperty('--upload-button-color', '#ffffff');
      root.style.setProperty('--upload-button-hover-background', 'rgba(255, 255, 255, 0.3)');
      root.style.setProperty('--upload-button-hover-border', 'rgba(255, 255, 255, 0.5)');
      root.style.setProperty('--loading-spinner-color', '#ffffff');
      
      // Contact page colors
      root.style.setProperty('--contact-background', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)');
      root.style.setProperty('--contact-title-color', '#ffffff');
      root.style.setProperty('--contact-subtitle-color', 'rgba(255, 255, 255, 0.9)');
      root.style.setProperty('--contact-card-background', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--contact-card-border', 'rgba(0, 0, 0, 0.1)');
      root.style.setProperty('--contact-info-title-color', '#2c3e50');
      root.style.setProperty('--contact-info-label-color', '#2c3e50');
      root.style.setProperty('--contact-info-value-color', '#34495e');
      root.style.setProperty('--contact-form-background', 'rgba(255, 255, 255, 0.95)');
      root.style.setProperty('--contact-form-title-color', '#2c3e50');
      root.style.setProperty('--contact-form-label-color', '#34495e');
      root.style.setProperty('--contact-form-border', '#e0e0e0');
      root.style.setProperty('--contact-form-input-background', '#ffffff');
      root.style.setProperty('--contact-form-input-color', '#2c3e50');
      root.style.setProperty('--contact-form-placeholder-color', '#95a5a6');
      root.style.setProperty('--contact-form-focus-border', '#667eea');
      root.style.setProperty('--social-link-background', 'rgba(102, 126, 234, 0.15)');
      root.style.setProperty('--social-link-color', '#667eea');
      root.style.setProperty('--social-link-border', 'rgba(102, 126, 234, 0.3)');
      root.style.setProperty('--social-link-hover-background', 'rgba(102, 126, 234, 0.25)');
      
      root.setAttribute('data-theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    startTransition(() => {
      setIsDarkMode(prev => !prev);
    });
  };

  const theme = {
    isDarkMode,
    toggleTheme,
    colors: {
      // Light mode colors
      light: {
        background: 'linear-gradient(135deg, #6495ED 0%, #4169E1 50%, #1E3A8A 100%)',
        bodyBackground: '#6495ED',
        text: '#ffffff',
        textSecondary: 'rgba(255, 255, 255, 0.8)',
        cardBackground: 'rgba(255, 255, 255, 0.1)',
        border: 'rgba(255, 255, 255, 0.2)',
        headerBackground: 'rgba(10, 10, 10, 0.95)',
        footerBackground: 'rgba(10, 10, 10, 0.95)',
        buttonPrimary: 'linear-gradient(135deg, #6495ED 0%, #4169E1 100%)',
        buttonSecondary: 'transparent',
        titleGradient: 'linear-gradient(135deg, #ffffff 0%, #e6f3ff 50%, #cce7ff 100%)',
      },
      // Dark mode colors
      dark: {
        background: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
        bodyBackground: '#0a0a0a',
        text: '#ffffff',
        textSecondary: 'rgba(255, 255, 255, 0.8)',
        cardBackground: 'rgba(255, 255, 255, 0.1)',
        border: 'rgba(255, 255, 255, 0.2)',
        headerBackground: 'rgba(10, 10, 10, 0.95)',
        footerBackground: 'rgba(10, 10, 10, 0.95)',
        buttonPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        buttonSecondary: 'transparent',
        titleGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
      }
    }
  };

  return (
    <ThemeContext.Provider value={theme}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
